/**
 * باز کردن لایهٔ `data` رایج در پاسخ Laravel / JsonResource.
 * اگر چند لایهٔ data تو در تو باشد، یک بار دیگر unwrap می‌کند (حداکثر عمق محدود).
 *
 * @param {*} payload
 * @param {number} [depth]
 * @returns {*}
 */
export function unwrapData(payload, depth = 0) {
  if (payload == null || typeof payload !== 'object') return payload;
  if (depth > 4) return payload;
  if ('data' in payload && payload.data !== undefined) {
    return unwrapData(payload.data, depth + 1);
  }
  return payload;
}

/**
 * تبدیل پاسخ به آرایه (لیست سازمان‌ها، جلسات و ...).
 *
 * @param {*} payload
 * @returns {unknown[]}
 */
export function unwrapList(payload) {
  const inner = unwrapData(payload);
  if (Array.isArray(inner)) return inner;
  if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data;
  return [];
}

/**
 * پاسخ paginate لاراول: { data: [], current_page, per_page, total }
 *
 * @param {*} payload
 * @returns {{ items: unknown[], current: number, perPage: number, total: number }}
 */
export function unwrapPaginated(payload) {
  // فقط یک لایه success.data باز شود تا meta صفحه‌بندی از دست نرود
  const inner = payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload;
  if (inner && typeof inner === 'object' && Array.isArray(inner.data)) {
    return {
      items: inner.data,
      current: Number(inner.current_page) || 1,
      perPage: Number(inner.per_page) || inner.data.length,
      total: Number(inner.total ?? inner.data.length),
    };
  }
  if (Array.isArray(inner)) {
    return { items: inner, current: 1, perPage: inner.length, total: inner.length };
  }
  return { items: [], current: 1, perPage: 15, total: 0 };
}

/**
 * پاسخ metric داشبورد عملیاتی:
 * `{ success, data: { title, filter, data: [], current_page, total } }`
 *
 * فقط یک لایهٔ envelope باز می‌شود تا آرایهٔ ردیف‌ها با meta صفحه‌بندی قاطی نشود.
 *
 * @param {*} payload
 * @returns {{ title: string|null, filter: string|null, resource: string|null, data: unknown[], current_page: number, last_page: number, per_page: number, total: number }}
 */
export function unwrapMetricPage(payload) {
  const empty = {
    title: null,
    filter: null,
    resource: null,
    data: [],
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0,
  };

  if (payload == null) return empty;

  if (Array.isArray(payload)) {
    return {
      ...empty,
      data: payload,
      per_page: payload.length || 25,
      total: payload.length,
    };
  }

  if (typeof payload !== 'object') return empty;

  let inner = payload;
  if (
    'success' in payload
    && 'data' in payload
    && payload.data
    && typeof payload.data === 'object'
    && !Array.isArray(payload.data)
  ) {
    inner = payload.data;
  }

  if (Array.isArray(inner)) {
    return {
      ...empty,
      data: inner,
      per_page: inner.length || 25,
      total: inner.length,
    };
  }

  const rows = Array.isArray(inner.data) ? inner.data : [];
  return {
    title: inner.title ?? null,
    filter: inner.filter ?? null,
    resource: inner.resource ?? null,
    data: rows,
    current_page: Number(inner.current_page) || 1,
    last_page: Number(inner.last_page) || 1,
    per_page: Number(inner.per_page) || 25,
    total: Number(inner.total ?? rows.length),
  };
}
